import { FC, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { SpreadsheetChange } from '@libs/types';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';

import { routes } from '@app/routes';
import { Badge } from '@components/Badge';
import { CalloutBanner } from '@components/CalloutBanner';
import { ConfirmationModal } from '@components/ConfirmationModal';
import { TooltipCard } from '@components/Tooltip';
import { useGetAssessmentById } from '@features/assessment';
import {
  RemediationDecision,
  RemediationItemStatus,
  RemediationItemType,
  RemediationRunStatus,
  TERMINAL_REMEDIATION_ITEM_STATUSES,
} from '@libs/constants';
import { useTrans } from '@libs/i18n';
import {
  ArrowLeftIcon,
  CaretRightIcon,
  CheckIcon,
  ChecksIcon,
  FilePlusIcon,
  ListChecksIcon,
  SpinnerIcon,
  XIcon,
} from '@phosphor-icons/react';

import {
  useFinalizeRemediationRun,
  useRemediationRun,
  useRemediationRunItems,
  useReviewRemediationChange,
  useReviewRemediationItem,
} from '../api/remediation-api';
import { GeneratedDocumentPreview } from '../components/GeneratedDocumentPreview';
import { RemediationReviewSkeleton } from '../components/RemediationSkeletons';
import {
  type ChangeFilter,
  ChangeList,
  FocusedChangePanel,
} from '../components/ReviewChangeRail';
import { OriginalPane, ProposedPane } from '../components/ReviewDocumentPanes';
import { ReviewDocumentRail } from '../components/ReviewDocumentRail';
import { ReviewGenerationPanel } from '../components/ReviewGenerationPanel';
import { SpreadsheetPane } from '../components/SpreadsheetPane';
import { useReviewCursor } from '../hooks/useReviewCursor';
import { itemErrorKey } from '../utils/item-error';
import { hasContentLanded } from '../utils/run-progress';
import { useScrollToSelected } from '../utils/use-scroll-to-selected';

/**
 * The rail takes a share of the canvas row rather than a fixed slab of pixels.
 * A fixed 380px read as "just right" on a 1600px screen and as "half the
 * document is gone" on a 1280px one, where the app's own sidebar has already
 * taken its cut. The bounds keep it legible at both ends: below ~300px the
 * before/after pair stops being readable, above ~420px it is just wasting the
 * canvas.
 */
const RAIL_WIDTH = 'clamp(300px, 30%, 420px)';

/** The same rail as an overlay, where it has the viewport to itself. */
const RAIL_DRAWER_WIDTH = 'min(420px, 88vw)';

/**
 * The rail is one scroll region, not three.
 *
 * It used to divide its height between a capped focused panel and the change
 * list, each scrolling its own body. Every one of those boundaries cut content
 * off mid-sentence with a scrollbar too short to notice, and a reviewer could
 * not tell whether a change simply ended or the panel had run out of room. Now
 * the panel and the list are their natural height and the rail scrolls past
 * both — one scrollbar, in the place the eye looks for it.
 */
const RAIL_SCROLL_SX = { overflowY: 'auto', overscrollBehavior: 'contain' } as const;

/** Small pane label, matching the prose canvas's ORIGINAL / PROPOSED strip. */
const PaneHeading: FC<{ label: string; ai?: boolean }> = observer(({ label, ai }) => {
  const theme = useTheme();
  return (
    <Typography
      sx={{
        px: { xs: 1.5, md: 2.5 },
        pt: 1.75,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        flexShrink: 0,
        color: ai ? theme.palette.ocean[700] : theme.palette.text.tertiary,
      }}
    >
      {label}
    </Typography>
  );
});

/**
 * Screen 6 — redlined review.
 *
 * Two-pane original/proposed document canvas with numbered footnote pins, a
 * focused-change rail on the trailing edge, and a bottom bar that finalizes the
 * run. GENERATE items have no earlier version to diff, so they show the
 * generated document and one whole-document decision instead.
 */
export const RemediationReviewPage: FC = observer(() => {
  const theme = useTheme();
  const { runId } = useParams<{ runId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTrans();
  const { enqueueSnackbar } = useSnackbar();

  const { data: run } = useRemediationRun(runId);
  // Reviewing starts as soon as the first document lands. The run stays
  // PROCESSING until the last one settles, so gating the whole screen on
  // READY_FOR_REVIEW made a reviewer wait on documents they were not reading.
  const stillGenerating =
    run?.status === RemediationRunStatus.PENDING || run?.status === RemediationRunStatus.PROCESSING;
  const openForReview = run?.status === RemediationRunStatus.READY_FOR_REVIEW || stillGenerating;
  const { data, isLoading, isError } = useRemediationRunItems(
    runId,
    openForReview,
    stillGenerating,
  );

  // The run knows which assessment it came from but not what that assessment is
  // *about*. Domain and framework are what place a document — "Access Control
  // Policy" alone does not say which part of which standard it answers to.
  const { data: assessment } = useGetAssessmentById(
    { assessmentId: run?.assessmentId ?? '' },
    { includeDomains: true },
  );

  const reviewItem = useReviewRemediationItem(runId ?? '');
  const reviewChange = useReviewRemediationChange(runId ?? '');
  const finalize = useFinalizeRemediationRun();

  // Only documents whose content has landed can be reviewed; the rest would
  // render as an empty pane.
  const allItems = useMemo(() => data?.items ?? [], [data]);
  const items = useMemo(
    () => allItems.filter((i) => hasContentLanded(i.status)),
    [allItems],
  );
  const stillWorking = allItems.length - items.length;
  // Whether the run has any blanks worth stopping for. Decided here rather than
  // by letting the next page redirect when it finds none: that page fetches the
  // profile template before it can answer, so a self-skipping version would
  // show a frame of itself on the way past.
  const hasPlaceholders = useMemo(
    () => items.some((item) => (item.aiMetadata?.placeholdersToFill?.length ?? 0) > 0),
    [items],
  );
  const [filter, setFilter] = useState<ChangeFilter>('all');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  // Under `lg` the canvas cannot afford a permanent rail — two document panes
  // and a 300px rail leave nothing readable. The rail becomes an overlay there
  // instead of disappearing: it is the only place a decision can be made, and a
  // review screen with no way to decide is not a smaller review screen.
  const compactRail = useMediaQuery(theme.breakpoints.down('lg'));
  // The document rail and the change rail both eat into the canvas, and the
  // canvas is two panes of prose side by side. Below `lg` the change rail has
  // already become an overlay to protect them; the document rail collapses to a
  // strip for the same reason rather than stacking a second column on a screen
  // that could not afford the first.
  const [docRailOpen, setDocRailOpen] = useState(!compactRail);

  const {
    item,
    changes,
    proseChanges,
    selectedId,
    openItemId,
    order,
    railOpen,
    setRailOpen,
    openItem,
    selectChange: selectFromDocument,
    selectFromRail,
    decideChange,
    navChange,
  } = useReviewCursor({
    items,
    focusItemId: searchParams.get('item'),
    keyboardEnabled: openForReview,
    compactRail,
    submitDecision: (itemId, changeId, decision) =>
      reviewChange.mutate({ itemId, changeId, decision }),
  });

  // With the rail scrolling as one piece, the focused panel can sit above the
  // fold once the reviewer has scrolled into a long change list. Selecting a
  // change is a request to read and decide it, so the rail comes back to the
  // panel — and only when it is actually out of view.
  const { scrollRef: railRef, targetRef: panelRef } = useScrollToSelected<
    HTMLDivElement,
    HTMLDivElement
  >(selectedId);

  // The domain lives on the AI's own content payload, which is the only place
  // it is recorded per item; a failed item has no content and so no domain to
  // name. The key is resolved against the assessment's domains rather than
  // shown raw — `access_control` is an id, not a label.
  const domainName = useMemo(() => {
    const key = item?.generatedContent?.domainKey;
    if (!key) return null;
    const domain = assessment?.assessmentDomains?.find((d) => d.frameworkDomain?.domainKey === key);
    return domain?.frameworkDomain?.name ?? null;
  }, [assessment, item]);
  const frameworkName = assessment?.framework?.name ?? null;

  const busy = reviewItem.isPending || reviewChange.isPending || finalize.isPending;


  const decided = changes.filter((c) => c.decision !== RemediationDecision.PENDING).length;
  // What "accept all" would actually touch — already-accepted changes are skipped.
  const pendingCount = changes.filter((c) => c.decision !== RemediationDecision.ACCEPTED).length;
  // Finalize needs the whole run decided, not just the documents on screen —
  // `allItems`, and only once nothing is still generating. The API enforces the
  // same thing; this keeps the button honest rather than letting it 409.
  const allItemsTerminal =
    !stillGenerating &&
    allItems.length > 0 &&
    allItems.every((i) => TERMINAL_REMEDIATION_ITEM_STATUSES.includes(i.status));

  // No bulk endpoint exists, so "accept all" is N per-change calls. Fine for a
  // deliberate, confirmed action; it is not offered as a casual one.
  const acceptAll = () => {
    if (!item) return;
    changes
      .filter((c) => c.decision !== RemediationDecision.ACCEPTED)
      .forEach((c) =>
        reviewChange.mutate({ itemId: item.itemId, changeId: c.changeId, decision: 'ACCEPTED' }),
      );
    setBulkOpen(false);
  };
  const rejectAll = () => {
    if (!item) return;
    changes
      .filter((c) => c.decision !== RemediationDecision.REJECTED)
      .forEach((c) =>
        reviewChange.mutate({ itemId: item.itemId, changeId: c.changeId, decision: 'REJECTED' }),
      );
  };

  // What finalize actually costs the user, and why it is worth confirming: it
  // renders every accepted document and forks the assessment into version N+1.
  // That version is left as a draft for them to submit, but the fork itself is
  // not reversible from here.
  const acceptedCount = allItems.filter(
    (i) => i.status === RemediationItemStatus.ACCEPTED,
  ).length;

  const onFinalize = () => {
    if (!runId) return;
    setFinalizeOpen(false);
    finalize.mutate(runId, {
      onSuccess: () => {
        enqueueSnackbar(t('app.remediation.toast.finalizeStarted'), { variant: 'success' });
        // The fork runs on the worker, so the new version does not exist yet.
        // The run page reports it landing and then links through to it.
        navigate(routes.remediationRun(runId));
      },
    });
  };

  const back = () => navigate(runId ? routes.remediationRun(runId) : routes.remediationList());

  // Terminal runs (COMPLETED / DISCARDED / FAILED) have nothing left to decide.
  if (run && !openForReview) {
    return (
      <Shell>
        <Box sx={{ p: 3 }}>
          <CalloutBanner tone="neutral" title={t('app.remediation.review.notReady')} />
        </Box>
      </Shell>
    );
  }
  // Working, but the first document has not landed yet.
  if (run && stillGenerating && items.length === 0 && !isLoading) {
    return (
      <Shell>
        <Box sx={{ p: 3 }}>
          <CalloutBanner tone="ai" filledIcon title={t('app.remediation.review.waitingTitle')}>
            {t('app.remediation.review.waitingBody')}
          </CalloutBanner>
        </Box>
      </Shell>
    );
  }
  if (isLoading || !run) {
    return (
      <Shell>
        <RemediationReviewSkeleton />
      </Shell>
    );
  }
  if (isError) {
    return (
      <Shell>
        <Box sx={{ p: 3 }}>
          <CalloutBanner tone="error" title={t('app.remediation.review.loadError')} />
        </Box>
      </Shell>
    );
  }

  const isGenerate =
    item?.type === RemediationItemType.GENERATE || item?.changeSet?.mode === 'WHOLE_DOCUMENT';
  const selectedChange = changes.find((c) => c.changeId === selectedId) ?? null;
  const selIdx = selectedId ? order.indexOf(selectedId) : -1;

  /**
   * The rail is the review surface, so it stays for every document that can be
   * reviewed — a generated one included. Its content is built once and placed
   * twice (inline column, or overlay when the canvas is too narrow to spare the
   * width) so the two placements cannot drift apart.
   *
   * A failed item is the one exception: there is nothing to decide, and the
   * canvas already explains why.
   */
  const railBody =
    item && item.status !== RemediationItemStatus.FAILED ? (
      <>
        <Box ref={panelRef} sx={{ p: 2 }}>
          {isGenerate ? (
            <ReviewGenerationPanel
              item={item}
              busy={busy}
              onDecide={(d) => reviewItem.mutate({ itemId: item.itemId, decision: d })}
            />
          ) : selectedChange ? (
            <FocusedChangePanel
              change={selectedChange}
              index={selIdx + 1}
              hasPrev={selIdx > 0}
              hasNext={selIdx >= 0 && selIdx < order.length - 1}
              busy={busy}
              onDecide={(d) => decideChange(selectedChange.changeId, d)}
              onNav={navChange}
            />
          ) : (
            <Paper
              elevation={0}
              sx={{
                border: `1px dashed ${theme.palette.grey[400]}`,
                borderRadius: 3.5,
                px: 2.25,
                py: 3,
                textAlign: 'center',
              }}
            >
              <Typography variant="textSM_semiBold" sx={{ display: 'block', mb: 0.5 }}>
                {t(
                  changes.length > 0
                    ? 'app.remediation.review.pickAChange'
                    : 'app.remediation.review.noChangesTitle',
                )}
              </Typography>
              <Typography variant="textXS_regular" sx={{ color: 'text.secondary' }}>
                {t(
                  changes.length > 0
                    ? 'app.remediation.review.pickAChangeSub'
                    : 'app.remediation.review.noChangesBody',
                )}
              </Typography>
            </Paper>
          )}
        </Box>
        {/* A generated document has no change set to list — its rail is the
            panel above and nothing else. */}
        {!isGenerate && (
          <ChangeList
            changes={changes}
            order={order}
            selectedId={selectedId}
            filter={filter}
            onFilter={setFilter}
            onSelect={selectFromRail}
          />
        )}
      </>
    ) : null;

  return (
    <Shell>
      {/* ── top bar ─────────────────────────────────────────────────────── */}
      <Box sx={{ borderBottom: `1px solid ${theme.palette.grey[300]}`, flexShrink: 0 }}>
        <Box
          sx={{
            px: 2.75,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12, minWidth: 0 }}>
            <Link
              component="button"
              type="button"
              onClick={() => navigate(routes.remediationList())}
              sx={{ fontSize: 12, color: 'text.secondary', textDecoration: 'none' }}
            >
              {t('app.remediation.list.title')}
            </Link>
            <CaretRightIcon size={11} color={theme.palette.text.tertiary} />
            <Typography sx={{ fontSize: 12, fontWeight: 500 }}>
              {t('app.remediation.review.title')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              fontSize: 12,
              color: busy ? theme.palette.ocean[700] : 'text.tertiary',
              flexShrink: 0,
            }}
          >
            {busy ? (
              <SpinnerIcon size={12} color={theme.palette.ocean[700]} />
            ) : (
              <CheckIcon size={12} color={theme.palette.green[700]} />
            )}
            {t(busy ? 'app.remediation.review.saving' : 'app.remediation.review.saved')}
          </Box>
        </Box>

        <Box
          sx={{
            px: 2.75,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Button variant="tertiary" onClick={back} sx={{ minWidth: 0, px: 1 }}>
              <ArrowLeftIcon size={18} />
            </Button>
            {/* Just the name now. Picking a document is the rail's job — a
                dropdown that was also the heading meant the one control naming
                the open document was the same one you had to open to see what
                else there was. */}
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                <Typography variant="h6" noWrap>
                  {item?.documentName ?? t('app.remediation.review.title')}
                </Typography>
                {items.length > 1 && (
                  <Typography
                    variant="textXS_regular"
                    sx={{ color: 'text.tertiary', whiteSpace: 'nowrap' }}
                  >
                    {t('app.remediation.review.docPosition', {
                      index: items.findIndex((d) => d.itemId === openItemId) + 1,
                      total: items.length,
                    })}
                  </Typography>
                )}
              </Box>

              {/* What this document answers to. The domain carries the same
                  blue pill it wears in the assessment summary so the two
                  screens name it the same way; the framework sits beside it as
                  plain text — it is the same for every document in the run and
                  a second pill would compete with the one that varies. */}
              {(domainName || frameworkName) && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 0.5,
                    minWidth: 0,
                    flexWrap: 'wrap',
                  }}
                >
                  {domainName && <Badge label={domainName} variant="DomainName" />}
                  {frameworkName && (
                    <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }} noWrap>
                      {frameworkName}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {!isGenerate && changes.length > 0 && (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    backgroundColor: theme.palette.grey[200],
                    borderRadius: 2.5,
                    px: 1.75,
                    py: 0.75,
                  }}
                >
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {t('app.remediation.review.progress', {
                      reviewed: decided,
                      total: changes.length,
                    })}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={(decided / changes.length) * 100}
                    sx={{ width: 80, height: 6, borderRadius: 99 }}
                  />
                </Box>
                <Button
                  variant="secondary"
                  startIcon={<ChecksIcon size={14} />}
                  disabled={busy}
                  onClick={() => setBulkOpen(true)}
                >
                  {t('app.remediation.review.acceptAll')}
                </Button>
                <Button
                  variant="secondary"
                  startIcon={<XIcon size={13} />}
                  disabled={busy}
                  onClick={rejectAll}
                >
                  {t('app.remediation.review.rejectAll')}
                </Button>
              </>
            )}

            {/* The way back to the rail once it is an overlay. Nothing else on
                this screen opens it, so it sits with the other document-level
                actions rather than floating over the canvas. */}
            {compactRail && railBody && (
              <Button
                variant="secondary"
                startIcon={<ListChecksIcon size={14} />}
                onClick={() => setRailOpen(true)}
              >
                {t('app.remediation.review.reviewPanel')}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Reviewing a partial run: say so, so an incomplete document switcher
          reads as "more coming" rather than "some are missing". */}
      {stillWorking > 0 && (
        <Box sx={{ px: 2.75, pt: 1.5, flexShrink: 0 }}>
          <CalloutBanner tone="ai" density="compact" filledIcon>
            {t('app.remediation.review.stillGenerating', { count: stillWorking })}
          </CalloutBanner>
        </Box>
      )}

      {/* ── documents + canvas + rail ───────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
        {allItems.length > 1 && (
          <ReviewDocumentRail
            // Every document, including the ones still being written. They were
            // filtered out entirely, so a run generating three more looked like
            // a run of two — and each one appearing later moved the list under
            // the reader. Listed and disabled instead: the work is visible, and
            // still not openable, because there is nothing behind it yet.
            items={allItems}
            activeId={openItemId}
            onSelect={openItem}
            open={docRailOpen}
            onToggle={() => setDocRailOpen((v) => !v)}
          />
        )}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            overflow: 'hidden',
            minWidth: 0,
            backgroundColor: theme.palette.grey[100],
          }}
        >
          {item?.status === RemediationItemStatus.FAILED ? (
            <Box sx={{ flex: 1, p: 3 }}>
              <CalloutBanner tone="error" title={t('app.remediation.review.itemFailedTitle')}>
                {/* Code-driven, never the AI's raw detail — see item-error.ts. */}
                {(() => {
                  const key = itemErrorKey(item);
                  return key ? t(key) : '';
                })()}
              </CalloutBanner>
            </Box>
          ) : item?.generatedContent?.format === 'spreadsheet' ? (
            /* Same before/after reading as prose. The uploaded file is not on the
               item, but v1 remediates columns only and every change records its
               header/key/order — so the original column set is reconstructable
               from the change set. A generated workbook has no "before". */
            <>
              {!isGenerate && (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    borderInlineEnd: `1px solid ${theme.palette.grey[300]}`,
                  }}
                >
                  <PaneHeading label={t('app.remediation.review.original')} />
                  <SpreadsheetPane
                    content={item.generatedContent}
                    changes={changes.filter((c) => 'columnKey' in c) as SpreadsheetChange[]}
                    variant="original"
                  />
                </Box>
              )}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                {!isGenerate && <PaneHeading label={t('app.remediation.review.proposed')} ai />}
                <SpreadsheetPane
                  content={item.generatedContent}
                  changes={changes.filter((c) => 'columnKey' in c) as SpreadsheetChange[]}
                  order={order}
                  selectedId={selectedId}
                  onSelect={selectFromDocument}
                />
              </Box>
            </>
          ) : isGenerate ? (
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  maxWidth: 700,
                  mx: 'auto',
                  p: 4,
                  border: `1px solid ${theme.palette.grey[300]}`,
                  borderRadius: 1,
                }}
              >
                {item?.generatedContent && (
                  <GeneratedDocumentPreview
                    content={item.generatedContent}
                    runId={runId ?? ''}
                    // The header above already names the document; without this
                    // the AI's own title section prints it a second time.
                    documentTitle={item.documentName}
                  />
                )}
              </Paper>
            </Box>
          ) : (
            <>
              {item?.sourceText ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    borderInlineEnd: `1px solid ${theme.palette.grey[300]}`,
                    minWidth: 0,
                  }}
                >
                  <OriginalPane
                    sourceText={item.sourceText}
                    changes={proseChanges}
                    order={order}
                    selectedId={selectedId}
                    onSelect={selectFromDocument}
                  />
                </Box>
              ) : null}
              {item?.generatedContent?.format === 'prose' && (
                <ProposedPane
                  content={item.generatedContent}
                  changes={proseChanges}
                  order={order}
                  selectedId={selectedId}
                  onSelect={selectFromDocument}
                  runId={runId ?? ''}
                />
              )}
            </>
          )}
        </Box>

        {!compactRail && railBody && (
          <Box
            ref={railRef}
            sx={{
              width: RAIL_WIDTH,
              flexShrink: 0,
              borderInlineStart: `1px solid ${theme.palette.grey[300]}`,
              backgroundColor: theme.palette.grey[200],
              minHeight: 0,
              ...RAIL_SCROLL_SX,
            }}
          >
            {railBody}
          </Box>
        )}
      </Box>

      {/* Below `lg` the same rail arrives over the canvas. `anchor="right"`
          follows the reading direction — MUI flips it in RTL — so it opens on
          the same edge it docks to on a wide screen. */}
      {compactRail && railBody && (
        <Drawer
          anchor="right"
          open={railOpen}
          onClose={() => setRailOpen(false)}
          PaperProps={{
            sx: {
              width: RAIL_DRAWER_WIDTH,
              backgroundColor: theme.palette.grey[200],
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.25,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              borderBottom: `1px solid ${theme.palette.grey[300]}`,
            }}
          >
            <Typography variant="textSM_semiBold" noWrap>
              {t('app.remediation.review.reviewPanel')}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setRailOpen(false)}
              aria-label={t('app.common.close')}
            >
              <XIcon size={16} />
            </IconButton>
          </Box>
          <Box ref={railRef} sx={{ flex: 1, minHeight: 0, ...RAIL_SCROLL_SX }}>
            {railBody}
          </Box>
        </Drawer>
      )}

      {/* ── bottom bar ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          borderTop: `1px solid ${theme.palette.grey[300]}`,
          px: 2.75,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        {/* Leading slot. The shortcut hint belongs to the per-change flow, so a
            generated document leaves this empty and its buttons sit right. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          {!isGenerate && (
            <Typography variant="textXS_regular" sx={{ color: 'text.tertiary' }}>
              {t('app.remediation.review.kbdHint')}
            </Typography>
          )}
        </Box>

        {/* Trailing slot. The whole-document decision now lives in the rail
            beside the generation detail it is made on, so this bar carries the
            run-level actions on every document — the same two buttons wherever
            the reviewer is in the run. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Button variant="tertiary" onClick={() => navigate(routes.remediationList())}>
            {t('app.remediation.review.saveForLater')}
          </Button>
          {/* Why the button is disabled is the thing a reviewer needs most, and
              it was carried by a `title` attribute — which browsers suppress on
              a disabled button, so the explanation only ever appeared once it
              was no longer needed. A tooltip on a wrapper still receives the
              hover, and it keeps saying something useful when enabled. */}
          <TooltipCard
            placement="top"
            enterDelay={200}
            description={
              stillGenerating
                ? t('app.remediation.review.finalizeWaiting')
                : !allItemsTerminal
                  ? t('app.remediation.review.finalizeBlocked')
                  : t('app.remediation.review.finalizeHint')
            }
          >
            <Box component="span" sx={{ display: 'inline-flex' }}>
              {/* Goes to the summary rather than straight to the confirm
                  dialog. Forking a version is irreversible, and a modal has
                  room for two sentences — not for what the run actually
                  produced, which is what the decision turns on. */}
              <Button
                variant="primary"
                startIcon={<CheckIcon size={16} />}
                disabled={!allItemsTerminal || busy}
                /* Via the blanks the AI could not fill, when there are any.
                   That list only exists because the organization profile was
                   missing those values, and this is the last moment the user is
                   looking at the documents that need them — after the summary
                   the run is applied and the question never comes back. */
                onClick={() =>
                  navigate(
                    hasPlaceholders
                      ? routes.remediationPlaceholders(runId ?? '')
                      : routes.remediationSummary(runId ?? ''),
                  )
                }
              >
                {t('app.remediation.review.finalize')}
              </Button>
            </Box>
          </TooltipCard>
        </Box>
      </Box>

      <ConfirmationModal
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        icon={<ChecksIcon size={20} />}
        iconColor="primary"
        title={t('app.remediation.review.acceptAll')}
        content={
          <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
            {t('app.remediation.review.acceptAllConfirm', { count: pendingCount })}
          </Typography>
        }
        buttons={[
          {
            label: t('app.common.cancel'),
            variant: 'outlined',
            onClick: () => setBulkOpen(false),
          },
          {
            label: t('app.remediation.review.acceptAll'),
            variant: 'contained',
            color: 'primary',
            startIcon: <ChecksIcon size={16} />,
            onClick: acceptAll,
          },
        ]}
      />

      {/* Finalize is the one irreversible step on this screen: it forks a new
          assessment version out of the accepted documents. The reviewer decides
          that, rather than discovering it from the screen they land on
          afterwards. */}
      <ConfirmationModal
        open={finalizeOpen}
        onClose={() => setFinalizeOpen(false)}
        icon={<FilePlusIcon size={20} />}
        iconColor="primary"
        title={t('app.remediation.review.finalizeConfirmTitle')}
        content={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
              {t('app.remediation.review.finalizeConfirmBody', { count: acceptedCount })}
            </Typography>
            <Typography variant="textSM_regular" sx={{ color: 'text.secondary' }}>
              {t('app.remediation.review.finalizeConfirmDraft')}
            </Typography>
          </Box>
        }
        buttons={[
          {
            label: t('app.common.cancel'),
            variant: 'outlined',
            onClick: () => setFinalizeOpen(false),
          },
          {
            label: t('app.remediation.review.finalizeConfirmCta'),
            variant: 'contained',
            color: 'primary',
            startIcon: <CheckIcon size={16} />,
            onClick: onFinalize,
          },
        ]}
      />
    </Shell>
  );
});

const Shell: FC<{ children: React.ReactNode }> = observer(({ children }) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'background.paper',
      borderRadius: 4,
      overflow: 'hidden',
    }}
  >
    {children}
  </Box>
));
